import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import '@adornis/bh-institution/client/bh-admin-einrichtungen-list.js';
import '@adornis/buchungsheld-components/authentication/bh-login.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Permission, Role } from '@adornis/buchungsheld-db/enums/roles.js';
import '@adornis/buchungsheld-theme/components/tr-tooltip.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import '@adornis/chemistry/elements/components/x-sidebar.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import '@adornis/cms/client/generated-form.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import '@adornis/translation/client/translation-editor.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser, isImpersonating } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { from, of } from 'rxjs';
import packagejson from '../../../../package.json';
import logo from '../../assets/buchungsheld_logo_small.png';
import '../../layout/bh-language-switch.js';
import './bh-page-admin-einrichtung.js';
import { adminEinrichtungRouter } from './bh-page-admin-einrichtung.js';
import './bh-page-admin-einrichtungs-verwaltung.js';
import './bh-page-admin-global-statistics.js';
import './global-settings/bh-page-admin-global-settings-mails.js';
import './global-settings/bh-page-admin-global-settings.js';
import { adminGlobalSettingsRouter } from './global-settings/bh-page-admin-global-settings.js';

const notAllowedPage = () => {
  return html`
    <x-empty-placeholder .img=${'close'} .text=${'Sie haben hierfür keine Berechtigung.'}></x-empty-placeholder>
  `;
};

const institutionGuard = (institutionID: string | undefined, content) => {
  if (!institutionID) throw new Error('institutionID not defined');
  // todo: check if view-settings are set and maybe update semester queryParms
  return permissionGuard(Permission.VIEW_ADMIN_DASHBOARD, () => content, {
    forbiddenTemplate: notAllowedPage(),
    context: institutionID,
  });
};

export const adminRouter = makeRouter(
  {
    root: {
      path: '/',
      render: params => {
        return html` <bh-admin-einrichtungen-list show-admin></bh-admin-einrichtungen-list> `;
      },
    },
    choose: {
      path: '/choose',
      render: () => html` <bh-admin-einrichtungen-list show-admin></bh-admin-einrichtungen-list> `,
    },
    settings: {
      path: '/settings*',
      render: () => html` <bh-page-admin-global-settings></bh-page-admin-global-settings> `,
      subRouter: adminGlobalSettingsRouter,
    },
    management: {
      path: '/management',
      render: () => html` <bh-page-admin-einrichtung-verwaltung></bh-page-admin-einrichtung-verwaltung>`,
    },
    statistics: {
      path: '/statistics',
      render: () => {
        const content = () => html` <bh-page-admin-global-statistics></bh-page-admin-global-statistics> `;
        return permissionGuard(Permission.VIEW_SUPER_ADMIN_SIDEBAR, content);
      },
    },
    'translation-editor': {
      path: '/translation-editor',
      render: () => html`
        <tr-flex space="md">
          <bh-breadcrumb .items=${[{ content: 'Übersetzungen', active: true }]}></bh-breadcrumb>
          <translation-editor></translation-editor>
        </tr-flex>
      `,
    },
    institution: {
      path: '/institution-details/:institutionID{/*}?',
      render: params => {
        console.log('page-admin', params);
        return institutionGuard(
          params.institutionID,
          html` <bh-page-admin-einrichtung institution-id=${params.institutionID}></bh-page-admin-einrichtung> `,
        );
      },
      subRouter: adminEinrichtungRouter,
    },
  },
  { routerName: 'admin-router', fallback: () => html` <bh-page-admin-einrichtung></bh-page-admin-einrichtung> ` },
);

export const BhAdminDashboard = webcomponent('bh-admin-dashboard', () => {
  return authenticationGuard(
    stateful(() => {
      const design = DesignSystem.currentTheme.getValue();
      const isImpersonatingUser = useObservable(() => isImpersonating, []);
      const user = useObservable(() => currentUser<User>(), []);
      const isSuperAdmin = useObservable(
        () => of(!!user?.roles.find(r => r.name === Role.SUPER_ADMIN && r.contexts.includes(GLOBAL_CONTEXT))),
        [user],
      );
      const institution = useObservable(
        () =>
          !adminRouter.variables.value.institutionID
            ? of(null)
            : from(getByID(Institution, adminRouter.variables.value.institutionID)(Institution.allFields)),
        [adminRouter.variables.value.institutionID],
      );

      return html`
        <x-flex h100>
          ${isImpersonatingUser
            ? html`
                <tr-flex
                  w100
                  horizontal
                  crossaxis-center
                  space-between
                  ${css({
                    background: design.colors.tone.warning,
                    padding: `${design.spacing.xs} ${design.spacing.md}`,
                  })}
                >
                  <x-flex horizontal crossaxis-center space="md">
                    <tr-icon outlined> warning </tr-icon>
                    <tr-text> ${clientTranslator.translate('management', 'currently_immitating')} </tr-text>
                  </x-flex>
                  <tr-button mode="outline" tone="error" icon="close" @click=${() => User.stopImpersonation()}>
                    ${clientTranslator.translate('management', 'stop_immitating')}
                  </tr-button>
                </tr-flex>
              `
            : nothing}

          <tr-flex horizontal flex ${css({ background: design.colors.tone.snowWhite, minHeight: '0' })}>
            <x-sidebar logoRouteTo="/admin" ${css({ background: '#fff', minWidth: '256px' })}>
              <img src=${logo} slot="logo" ${css({ width: '30%', margin: 'auto' })} />
              <x-flex space="sm" ${css({ padding: design.spacing.md })}>
                ${institutionOptions({ institution, user })} ${globalAdminItems(isSuperAdmin ?? false)}
              </x-flex>

              <x-flex slot="bottom" space="sm" horizontal center w100>
                <bh-language-switch></bh-language-switch>

                <tr-button-small
                  .color=${design.colors.tone.black}
                  mode="outline"
                  icon="home"
                  .action=${() => goTo('/')}
                >
                  <tr-tooltip .text=${html` ${clientTranslator.translate('SIDEBAR', 'home')} `}></tr-tooltip>
                </tr-button-small>
                <tr-button-small
                  .color=${design.colors.tone.black}
                  mode="outline"
                  icon="logout"
                  .action=${() => logout().then(() => goTo('/'))}
                >
                  <tr-tooltip .text=${html` ${clientTranslator.translate('SIDEBAR', 'logout')} `}></tr-tooltip>
                </tr-button-small>
                <tr-text tone="placeholder" ${css({ marginLeft: 'auto', marginTop: 'auto' })}>
                  v.${packagejson.version}</tr-text
                >
              </x-flex>
            </x-sidebar>

            <x-flex flex padding="lg" ${css({ overflowY: 'auto', boxSizing: 'border-box', paddingLeft: '130px' })}>
              ${watch(adminRouter.outlet)}
            </x-flex>
          </tr-flex>
        </x-flex>
      `;
    }),
    html` <x-flex h100 w100 center crossaxis-center flex> <bh-login></bh-login> </x-flex> `,
  );
});

function globalAdminItems(isSuperAdmin: boolean) {
  if (!isSuperAdmin) return nothing;

  return html`
    <tr-sidebar-item icon="settings" href="/admin/settings" .text=${clientTranslator.translate('SIDEBAR', 'settings')}>
    </tr-sidebar-item>

    <tr-sidebar-item
      href="/admin/management"
      icon="admin_panel_settings"
      .text=${clientTranslator.translate('SIDEBAR', 'admins')}
    >
    </tr-sidebar-item>

    <tr-sidebar-item
      icon="trending_up"
      href="/admin/statistics"
      .text=${clientTranslator.translate('SIDEBAR', 'statistics')}
    >
    </tr-sidebar-item>

    <tr-sidebar-item
      icon="translate"
      href="/admin/translation-editor"
      .text=${clientTranslator.translate('SIDEBAR', 'translations')}
    >
    </tr-sidebar-item>
  `;
}

const institutionOptions = ({ institution, user }: { institution: Maybe<Institution>; user: Maybe<User> }) => {
  return html`
    <tr-sidebar-item
      .activeSubitem=${!!institution}
      icon="apartment"
      .validateActive=${(currentUrl: string, href?: string) =>
        currentUrl === '/admin' || currentUrl === '/admin/choose'}
      .text=${institution?.name ?? clientTranslator.translate('SIDEBAR', 'chooseInstitution')}
      .href=${institution ? '/admin' : '/admin/choose'}
      .subItems=${institution
        ? [
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD)
              ? {
                  href: `/admin/institution-details/${institution._id}/home`,
                  icon: 'home',
                  text: clientTranslator.translate('SIDEBAR', 'dashboard'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_WORKSHOPS)
              ? {
                  href: `/admin/institution-details/${institution._id}/workshops`,
                  icon: 'list',
                  text: clientTranslator.translate('SIDEBAR', 'workshops'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_WORKSHOPS)
              ? {
                  href: `/admin/institution-details/${institution._id}/sets-workshop`,
                  icon: 'list',
                  text: clientTranslator.translate('SIDEBAR', 'workshop_sets'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_COUNSELINGS) && institution._feature_counselings
              ? {
                  href: `/admin/institution-details/${institution._id}/counselings`,
                  icon: 'list',
                  text: clientTranslator.translate('SIDEBAR', 'counselings'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_BUNDLES)
              ? {
                  href: `/admin/institution-details/${institution._id}/bundles`,
                  icon: 'backpack',
                  text: clientTranslator.translate('SIDEBAR', 'bundles'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_TIMEFRAMES)
              ? {
                  href: `/admin/institution-details/${institution._id}/timeframes`,
                  icon: 'schedule',
                  text: clientTranslator.translate('timeframe', 'plural'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_CATEGORIES)
              ? {
                  href: `/admin/institution-details/${institution._id}/categories`,
                  icon: 'schedule',
                  text: clientTranslator.translate('SIDEBAR', 'categories'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS)
              ? {
                  href: `/admin/institution-details/${institution._id}/users`,
                  icon: 'person',
                  text: clientTranslator.translate('SIDEBAR', 'users'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_LECTURERS)
              ? {
                  href: `/admin/institution-details/${institution._id}/lecturers`,
                  icon: 'person',
                  text: clientTranslator.translate('SIDEBAR', 'lecturers'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_FORM_CONFIG)
              ? {
                  href: `/admin/institution-details/${institution._id}/form-config`,
                  icon: 'file',
                  text: clientTranslator.translate('SIDEBAR', 'form-config'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_SETTINGS)
              ? {
                  href: `/admin/institution-details/${institution._id}/settings`,
                  icon: 'settings',
                  text: clientTranslator.translate('SIDEBAR', 'settings'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_SETTINGS)
              ? {
                  href: `/admin/institution-details/${institution._id}/statistics`,
                  icon: 'statistics',
                  text: clientTranslator.translate('SIDEBAR', 'statistics'),
                }
              : null,
            user?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_EXPORTS)
              ? {
                  href: `/admin/institution-details/${institution._id}/exports`,
                  icon: 'download',
                  text: clientTranslator.translate('SIDEBAR', 'imExport'),
                }
              : null,
          ].filter(data => !!data)
        : []}
    >
    </tr-sidebar-item>
  `;
};
